import { createChart, CandlestickSeries, createSeriesMarkers } from 'lightweight-charts';
import { useEffect, useRef, useCallback } from 'react';

const toSec = (iso) => Math.floor(new Date(iso).getTime() / 1000);

export default function BacktestChart({ candles, trades, selectedTrade }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const priceLinesRef = useRef([]);

  // ── Montaje del chart (una sola vez) ────────────────────────────
  useEffect(() => {
    const chart = createChart(containerRef.current, {
      layout: { background: { color: '#0A0A0F' }, textColor: '#9CA3AF',
                fontFamily: 'JetBrains Mono, monospace' },
      grid: { vertLines: { color: '#1F2937' }, horzLines: { color: '#1F2937' } },
      crosshair: { mode: 0 },
      height: 500,
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#10B981', downColor: '#EF4444', borderVisible: false,
      wickUpColor: '#10B981', wickDownColor: '#EF4444',
    });
    chartRef.current = chart;
    seriesRef.current = series;

    const onResize = () =>
      chart.applyOptions({ width: containerRef.current.clientWidth });
    onResize();
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); chart.remove(); };
  }, []);

  // ── Datos + markers ─────────────────────────────────────────────
  useEffect(() => {
    if (!seriesRef.current || !candles?.length) return;
    seriesRef.current.setData(candles);

	const markers = (trades ?? [])
	      .flatMap(t => {
	        if (!t.entryTime) return [];          // backtests pre-fix: sin marker
	        return [
	          {
	            time: toSec(t.entryTime),
	            position: t.direction === 'BUY' ? 'belowBar' : 'aboveBar',
	            color: t.result === 'WIN' ? '#10B981'
	                 : t.result === 'LOSS' ? '#EF4444' : '#9CA3AF',
	            shape: t.direction === 'BUY' ? 'arrowUp' : 'arrowDown',
	            text: `${t.direction} ${t.pnlAtR != null
	                    ? (t.pnlAtR > 0 ? '+' : '') + t.pnlAtR.toFixed(1) + 'R' : ''}`,
	          },
	          t.exitTime && {
	            time: toSec(t.exitTime),
	            position: 'inBar', color: '#9CA3AF', shape: 'circle', text: 'exit',
	          },
	        ];
	      })
	      .filter(Boolean)
	      .sort((a, b) => a.time - b.time);  // v5 exige markers ordenados

    createSeriesMarkers(seriesRef.current, markers);
    chartRef.current.timeScale().fitContent();
  }, [candles, trades]);

  // ── Trade seleccionado: líneas SL/TP/entry + zoom ───────────────
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    priceLinesRef.current.forEach(l => series.removePriceLine(l));
    priceLinesRef.current = [];
    if (!selectedTrade) return;

    const mk = (price, color, title) => price && priceLinesRef.current.push(
      series.createPriceLine({ price: Number(price), color, lineWidth: 1,
                               lineStyle: 2, axisLabelVisible: true, title }));
    mk(selectedTrade.entryPrice, '#F59E0B', 'ENTRY');
    mk(selectedTrade.slPrice,   '#EF4444', 'SL');
    mk(selectedTrade.tpPrice,   '#10B981', 'TP');

	if (selectedTrade.entryTime) {
	      const t = toSec(selectedTrade.entryTime);
	      chartRef.current.timeScale().setVisibleRange({ from: t - 10800, to: t + 10800 });
	    }  // ±3h alrededor de la entrada
  }, [selectedTrade]);

  return <div ref={containerRef} className="w-full rounded-lg overflow-hidden" />;
}